/**
 * FbShell - the shared frame for every FB-onboarding screen.
 *
 * Mirrors Keiki's layout (top bar with back + centered brand, thin progress
 * bar with "x/total", a mascot pointing at a soft speech bubble that asks the
 * question, answer area, sticky bottom CTA) in Time To Talk's cream + orange
 * skin with Timo as the mascot. Styling intentionally stays SOFT: the rounded
 * Fredoka font, medium/semibold weights (never heavy), gentle shadows and
 * plenty of air, so it reads clean and friendly like Keiki rather than shouty.
 *
 * Responsiveness is a hard requirement (iOS Safari + the Facebook in-app
 * browser, Android Chrome, phones AND tablets):
 *   - 100dvh so mobile browser chrome never crops the CTA
 *   - env(safe-area-inset-*) padding for notch / home indicator / cutouts
 *   - max-w-md centers the column on tablets and desktop
 *   - the CTA sits in a sticky footer that always stays reachable
 */

import React from 'react';
import { motion } from 'framer-motion';
import { CaretLeft, Image as ImageIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import {
  useKeyboardInset,
  useScrollFocusedIntoView,
  keyboardFixedShellStyle,
} from '@/hooks/useKeyboardInset';
import timoMascot from '@/assets/timo-onboarding.webp';

export const FB = {
  cream: '#FFFBF7',
  orange: '#FF8C5A',
  orangeSoft: '#FFA877',
  ink: '#3A3746', // slightly softer than pure charcoal
  inkSoft: '#8A8494',
  cardTint: '#FFF3EA', // soft orange wash for selected cards / image slots
  track: '#F1E8DF', // progress-bar track
  // Rounded, friendly stack. Fredoka is loaded in index.html.
  font: "'Fredoka', system-ui, -apple-system, 'Segoe UI', sans-serif",
};

/**
 * UI chrome copy: buttons, links, aria-labels, loading and error states.
 *
 * The QUESTIONS live in ../steps.js + ../locales/*.json and are translated by
 * localizeSteps(). This dictionary covers everything around them, which used to
 * be hardcoded English, so a Lithuanian parent answered Lithuanian questions by
 * tapping a button marked "Continue".
 *
 * Same contract as the locale JSON files: English is the complete set, every
 * other language is an override layer, and a missing key falls back to English
 * rather than to blank. de and no ship only the lines that read naturally to a
 * native speaker; the rest are deliberately absent so they render as honest
 * English instead of guessed German or Norwegian.
 *
 * `{name}` tokens follow the steps.js convention and are substituted at render
 * time. Languages whose grammar would need a case the funnel cannot produce
 * (lt, ru) drop the token, exactly as their locale files do.
 */
const CHROME = {
  en: {
    continue: 'Continue',
    skip: 'Skip',
    saving: 'Saving...',
    goBack: 'Go back',
    logIn: 'Log in',
    legalPrefix: 'By continuing you agree to our',
    legalAnd: 'and',
    terms: 'Terms',
    privacy: 'Privacy Policy',
    googleCta: 'Continue with Google',
    or: 'or',
    emailPlaceholder: 'you@email.com',
    emailNote: 'Only used to create your login and save your progress. No spam, ever.',
    scoreToday: 'Score today',
    scoreTodayNamed: '{name}’s score today',
    scoreBasis: 'Based on your answers. Timo will focus on the areas below.',
    voiceYoung: 'Younger, playful',
    voiceWarm: 'Warm, friendly',
    googleError: 'Could not start Google sign-in. Try email instead.',
  },
  lt: {
    continue: 'Tęsti',
    skip: 'Praleisti',
    saving: 'Išsaugoma...',
    goBack: 'Grįžti atgal',
    logIn: 'Prisijungti',
    legalPrefix: 'Tęsdami sutinkate su mūsų',
    legalAnd: 'ir',
    terms: 'Naudojimo sąlygomis',
    privacy: 'Privatumo politika',
    googleCta: 'Tęsti su Google',
    or: 'arba',
    emailNote: 'Naudojame tik prisijungimui sukurti ir pažangai išsaugoti. Jokio šlamšto.',
    // Lithuanian needs the genitive of the name here, which the funnel cannot
    // build from a free-text field, so the name is dropped (as in lt.json).
    scoreToday: 'Šiandienos rezultatas',
    scoreTodayNamed: 'Šiandienos rezultatas',
    scoreBasis: 'Pagal jūsų atsakymus. Timo daugiausia dėmesio skirs žemiau nurodytoms sritims.',
    voiceYoung: 'Jaunesnis, žaismingas',
    voiceWarm: 'Šiltas, draugiškas',
    googleError: 'Nepavyko pradėti prisijungimo su Google. Pabandykite el. paštu.',
  },
  ru: {
    continue: 'Продолжить',
    skip: 'Пропустить',
    saving: 'Сохраняем...',
    goBack: 'Назад',
    logIn: 'Войти',
    legalPrefix: 'Продолжая, вы соглашаетесь с нашими',
    legalAnd: 'и',
    terms: 'Условиями использования',
    privacy: 'Политикой конфиденциальности',
    googleCta: 'Продолжить с Google',
    or: 'или',
    emailNote: 'Нужен только для входа и сохранения прогресса. Никакого спама.',
    // Same reason as lt: the name would need the genitive case.
    scoreToday: 'Сегодняшний результат',
    scoreTodayNamed: 'Сегодняшний результат',
    scoreBasis: 'На основе ваших ответов. Тимо сосредоточится на областях ниже.',
    voiceYoung: 'Молодой, игривый',
    voiceWarm: 'Тёплый, дружелюбный',
    googleError: 'Не удалось войти через Google. Попробуйте по электронной почте.',
  },
  es: {
    continue: 'Continuar',
    skip: 'Omitir',
    saving: 'Guardando...',
    goBack: 'Volver',
    logIn: 'Iniciar sesión',
    legalPrefix: 'Al continuar, aceptas nuestros',
    legalAnd: 'y nuestra',
    terms: 'Términos',
    privacy: 'Política de privacidad',
    googleCta: 'Continuar con Google',
    or: 'o',
    emailPlaceholder: 'tu@correo.com',
    emailNote: 'Solo se usa para crear tu cuenta y guardar tu progreso. Nada de spam.',
    scoreToday: 'Puntuación de hoy',
    scoreTodayNamed: 'La puntuación de {name} hoy',
    scoreBasis: 'Según tus respuestas. Timo se centrará en las áreas de abajo.',
    voiceYoung: 'Más joven, juguetón',
    voiceWarm: 'Cálido, amigable',
    googleError: 'No se pudo iniciar sesión con Google. Prueba con el correo electrónico.',
  },
  fr: {
    continue: 'Continuer',
    skip: 'Passer',
    saving: 'Enregistrement...',
    goBack: 'Retour',
    logIn: 'Se connecter',
    legalPrefix: 'En continuant, vous acceptez nos',
    legalAnd: 'et notre',
    terms: 'Conditions d’utilisation',
    privacy: 'Politique de confidentialité',
    googleCta: 'Continuer avec Google',
    or: 'ou',
    emailPlaceholder: 'vous@email.com',
    emailNote: 'Utilisé uniquement pour créer votre compte et sauvegarder votre progression. Aucun spam.',
    scoreToday: 'Score du jour',
    scoreTodayNamed: 'Le score de {name} aujourd’hui',
    scoreBasis: 'D’après vos réponses. Timo se concentrera sur les domaines ci-dessous.',
    voiceYoung: 'Plus jeune, enjoué',
    voiceWarm: 'Chaleureux, amical',
    googleError: 'Impossible de lancer la connexion Google. Essayez plutôt avec l’e-mail.',
  },
  de: {
    continue: 'Weiter',
    skip: 'Überspringen',
    saving: 'Wird gespeichert...',
    goBack: 'Zurück',
    logIn: 'Anmelden',
    legalPrefix: 'Wenn Sie fortfahren, akzeptieren Sie unsere',
    legalAnd: 'und die',
    terms: 'Nutzungsbedingungen',
    privacy: 'Datenschutzerklärung',
    googleCta: 'Weiter mit Google',
    or: 'oder',
    emailNote: 'Wird nur für Ihren Login und zum Speichern des Fortschritts verwendet. Kein Spam.',
    scoreToday: 'Heutiges Ergebnis',
    scoreTodayNamed: 'Heutiges Ergebnis für {name}',
    scoreBasis: 'Basierend auf Ihren Antworten. Timo konzentriert sich auf die folgenden Bereiche.',
    voiceYoung: 'Jünger, verspielt',
    voiceWarm: 'Warm, freundlich',
    googleError: 'Die Google-Anmeldung konnte nicht gestartet werden. Versuchen Sie es mit E-Mail.',
  },
  no: {
    continue: 'Fortsett',
    skip: 'Hopp over',
    saving: 'Lagrer...',
    goBack: 'Tilbake',
    logIn: 'Logg inn',
    legalPrefix: 'Ved å fortsette godtar du våre',
    legalAnd: 'og vår',
    terms: 'Vilkår',
    privacy: 'Personvernerklæring',
    googleCta: 'Fortsett med Google',
    or: 'eller',
    emailNote: 'Brukes bare til å opprette innloggingen din og lagre fremgangen. Ingen spam.',
    scoreToday: 'Resultat i dag',
    scoreTodayNamed: 'Resultat i dag for {name}',
    scoreBasis: 'Basert på svarene dine. Timo fokuserer på områdene nedenfor.',
    voiceYoung: 'Yngre, leken',
    voiceWarm: 'Varm, vennlig',
    googleError: 'Kunne ikke starte Google-innloggingen. Prøv med e-post i stedet.',
  },
};

const chromeCache = new Map();

/**
 * Chrome strings for a language, English-completed.
 *
 * `lang` is the funnel language (index.jsx passes funnelLang, so the chrome can
 * never disagree with the questions on screen). When it is missing - the
 * post-payment continue page renders these same screens without it - we fall
 * back to the stored app language rather than to English, so the buttons still
 * match the questions there. Reading localStorage directly keeps this usable
 * outside LanguageProvider, which throws when there is no provider.
 */
export function getChrome(lang) {
  let key = lang;
  if (!key) {
    try {
      key = localStorage.getItem('ttt-language') || 'en';
    } catch {
      key = 'en';
    }
  }
  const cached = chromeCache.get(key);
  if (cached) return cached;
  const merged = { ...CHROME.en, ...(CHROME[key] || {}) };
  chromeCache.set(key, merged);
  return merged;
}

/** Substitute the {name} token, the same way steps.js copy does. */
export const fillName = (text, name) =>
  typeof text === 'string' ? text.replace(/\{name\}/g, name || '') : text;

/**
 * ImageSlot - a rounded placeholder that shows an illustration when `src` is
 * set, and a soft peach placeholder (with optional emoji/label) until then.
 * Lets the user drop generated images in later without touching layout.
 */
export const ImageSlot = ({
  src,
  alt = '',
  ratio = '16 / 10',
  emoji,
  label,
  className,
  rounded = 'rounded-[28px]',
}) => (
  <div
    className={cn('w-full overflow-hidden flex items-center justify-center', rounded, className)}
    style={{ aspectRatio: ratio, backgroundColor: FB.cardTint }}
  >
    {src ? (
      <img src={src} alt={alt} draggable="false" className="w-full h-full object-cover" />
    ) : emoji ? (
      <span className="text-[56px] leading-none select-none">{emoji}</span>
    ) : (
      <div className="flex flex-col items-center gap-1.5" style={{ color: '#E4B99C' }}>
        <ImageIcon size={30} weight="light" />
        {label && <span className="text-[12px] font-medium">{label}</span>}
      </div>
    )}
  </div>
);

/** Thin progress bar + "current/total" label, matching Keiki. */
const ProgressBar = ({ current, total }) => {
  const pct = Math.max(0, Math.min(100, (current / total) * 100));
  return (
    <div className="flex items-center gap-3 w-full">
      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: FB.track }}>
        <motion.div
          className="h-full rounded-full"
          style={{ backgroundColor: FB.orange }}
          initial={false}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
        />
      </div>
      <span className="text-[13px] font-medium tabular-nums" style={{ color: '#C3B7AA' }}>
        {current}/{total}
      </span>
    </div>
  );
};

/** Timo + speech bubble that asks the current question. */
const MascotAsk = ({ question, sub }) => (
  <div className="flex items-start gap-2 sm:gap-3">
    <motion.img
      src={timoMascot}
      alt="Timo"
      draggable="false"
      initial={{ scale: 0.92, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.35 }}
      className="w-[76px] h-[76px] sm:w-24 sm:h-24 object-contain flex-shrink-0"
    />
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.08 }}
      className="relative bg-white rounded-[26px] rounded-tl-lg px-5 py-4 shadow-[0_4px_18px_rgba(45,49,66,0.06)] mt-2.5 flex-1"
    >
      <p className="font-semibold leading-snug text-[18px] sm:text-[20px]" style={{ color: FB.ink }}>
        {question}
      </p>
      {sub && (
        <p className="mt-1.5 text-[14px] leading-snug font-normal" style={{ color: FB.inkSoft }}>
          {sub}
        </p>
      )}
      {/* little tail pointing back to Timo */}
      <div className="absolute top-5 -left-1.5 w-3 h-3 bg-white rotate-45" style={{ borderRadius: '2px' }} />
    </motion.div>
  </div>
);

const FbShell = ({
  question,
  sub,
  currentStep,
  totalSteps,
  showProgress = true,
  showMascot = true,
  onBack,
  showBack = true,
  showLogin = false,
  lang,
  children,
  footer,
}) => {
  const t = getChrome(lang);
  // The name and email steps both raise the software keyboard. iOS does not
  // shrink the layout viewport for it, so on a `fixed inset-0` shell the input
  // AND the sticky Continue button under it end up behind the keys, on the one
  // screen in this funnel that creates the account. Ending the shell where the
  // keyboard begins hands the problem to the scroll area below, which already
  // handles a short viewport correctly. Returns {} when no keyboard is up, so
  // every other screen renders byte-identically to before.
  const keyboardInset = useKeyboardInset();
  useScrollFocusedIntoView(keyboardInset);
  return (
    <div
      className="fb-funnel fixed inset-0 flex flex-col"
      style={{
        backgroundColor: FB.cream,
        fontFamily: FB.font,
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        ...keyboardFixedShellStyle(keyboardInset),
      }}
    >
      {/* Top bar: back + centered brand */}
      <div className="flex-shrink-0 px-4 pt-3 pb-1">
        <div className="mx-auto w-full max-w-md flex items-center justify-between">
          {/* 44x44 hit area, 36px disc. The button used to BE 36x36, which is
              under the touch minimum on every phone in the matrix, and it is the
              only way back through the funnel. The disc is unchanged; it now
              sits centred in a 44pt box, which also lines its left edge up with
              the px-5 body below instead of the px-4 bar. */}
          {showBack ? (
            <button
              onClick={onBack}
              aria-label={t.goBack}
              className="h-11 w-11 flex items-center justify-center active:scale-95 touch-manipulation"
            >
              <span className="w-9 h-9 rounded-full bg-white/80 flex items-center justify-center">
                <CaretLeft className="h-[18px] w-[18px]" weight="bold" style={{ color: FB.inkSoft }} aria-hidden />
              </span>
            </button>
          ) : (
            <div className="w-11" />
          )}
          <span className="font-semibold tracking-tight text-[16px]" style={{ color: FB.orange }}>
            Timo Helps
          </span>
          {/* "Log in" for returning parents (Keiki has the same top-right link).
              text-right + leading-tight so a longer label ("Iniciar sesion",
              "Prisijungti") wraps inside the bar instead of pushing the brand. */}
          {showLogin ? (
            <a
              href="/login"
              className="inline-flex min-h-[44px] items-center justify-end text-[14px] font-medium leading-tight text-right touch-manipulation"
              style={{ color: FB.inkSoft }}
            >
              {t.logIn}
            </a>
          ) : (
            <div className="w-11" />
          )}
        </div>
      </div>

      {/* Progress bar */}
      {showProgress && (
        <div className="flex-shrink-0 px-4 pt-1 pb-2">
          <div className="mx-auto w-full max-w-md">
            <ProgressBar current={currentStep} total={totalSteps} />
          </div>
        </div>
      )}

      {/* Scrollable body - momentum + contained overscroll so it never feels
          "stuck" on iOS/in-app browsers; extra bottom padding keeps the last
          field clear of the sticky footer. */}
      <div
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <div className="mx-auto w-full max-w-md flex flex-col min-h-full pb-3">
          {showMascot && (
            <div className="pt-3 pb-6">
              <MascotAsk question={question} sub={sub} />
            </div>
          )}
          <div className={cn('flex-1 flex flex-col', !showMascot && 'pt-2')}>{children}</div>
        </div>
      </div>

      {/* Sticky footer CTA */}
      {footer && (
        <div
          className="flex-shrink-0 px-5 pt-3 pb-5 bg-transparent"
          style={{
            boxShadow: '0 -8px 20px -12px rgba(45,49,66,0.12)',
          }}
        >
          <div className="mx-auto w-full max-w-md">{footer}</div>
        </div>
      )}
    </div>
  );
};

/** Primary orange CTA - soft, rounded, medium weight (never heavy). */
export const FbButton = ({ children, onClick, disabled = false, className }) => (
  <motion.button
    whileTap={disabled ? undefined : { scale: 0.98 }}
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'w-full py-[15px] rounded-[20px] font-semibold text-[17px] text-white touch-manipulation',
      'shadow-[0_6px_18px_rgba(255,140,90,0.28)] transition-[opacity,filter] active:brightness-95',
      'disabled:opacity-40 disabled:shadow-none',
      className
    )}
    style={{ backgroundColor: FB.orange }}
  >
    {children}
  </motion.button>
);

export default FbShell;
